import Vue from "vue";
import {
    deleteLookedChatFromStorage,
    getConversationsMeta,
    getLookedChatIds,
    getMyChatIds,
    pushLookedChatId,
    setConversationsMeta,
    setMyChatIds,
    setWatchingChatIds,
} from "../../utils/chat";
import {
    addNoteToConversation,
    loadOldConversations,
    loadOldConversationsAsPaged as loadOldConversationsAsPagedVal,
    loadOldMessages,
} from "../../api/chat";
import { log2 } from "../../utils/log2";
import { sendSocketMessage } from "../../utils/socketStream";
import * as _ from "lodash";
import store from "../index";
import { PHONE_UTILS } from "../../utils/phone";
import { i18n } from "../../language";

const log = log2("ChatStore");
let chatBarIndex = 1;

const defaultState = () => ({
    observing: [],
    conversations: [],
    activeConversation: null,
    previousConversation: null,
    privateMessageConversation: null,
    overdueTime: null,
    activeAgentRateConversationCount: null,
    openChats: [],
    beepPlayedChatIds: [],
    usersPrivateChatChannelMessages: [],
    stats: {
        total: 0,
        free: 0,
        waiting: 0,
        late: 0,
    },
    conversationsMeta: getConversationsMeta() || {},
    myChatIds: getMyChatIds(),
    autoTranslate: [],
});
const chat = {
    namespaced: true,
    state: {
        observing: defaultState().observing,
        conversations: defaultState().conversations,
        activeConversation: defaultState().activeConversation,
        fakeActiveConversation: null,
        previousConversation: defaultState().previousConversation,
        privateMessageConversation: defaultState().privateMessageConversation,
        overdueTime: defaultState().overdueTime,
        activeAgentRateConversationCount: defaultState().activeAgentRateConversationCount,
        openChats: defaultState().openChats,
        beepPlayedChatIds: defaultState().beepPlayedChatIds,
        usersPrivateChatChannelMessages: defaultState().usersPrivateChatChannelMessages,
        isChatHistoryOpen: false,
        stats: defaultState().stats,
        latestRows: [],
        latestChatbotChats: [],
        lookedConversationIds: getLookedChatIds(),
        conversationsMeta: defaultState().conversationsMeta,
        myChatIds: defaultState().myChatIds,
        autoTranslate: defaultState().autoTranslate,
    },
    getters: {
        getActiveConversations: (state) => {
            return state.conversations;
        },
        getObservingConversationIds: (state) => {
            return _.uniq(state.observing);
        },
        getMyActiveConversations: (state, getters, rootState) => {
            const allChats = state.conversations.map((x) => ({
                ...x,
                meta: state.conversationsMeta[x._id] || {},
            }));
            const myConversations = rootState.auth.user
                ? allChats.filter((c) => c.meta.active === true && c.users.includes(rootState.auth.user._id))
                : [];
            const watching = allChats.filter((x) => state.observing.includes(x._id));
            log("getMyActiveConversations", {
                user: rootState.auth.user,
                myConversations,
                conversations: state.conversations,
                observing: state.observing,
                watching,
            });
            let result = _.uniqBy([...myConversations, ...watching], (q) => q._id);
            result = _.orderBy(result, ["meta.chatBarOrder"], ["asc"]);

            if (
                Math.ceil(result.length / rootState.app.chatBarPageSize) < rootState.app.chatBarCurrentPage &&
                rootState.app.chatBarCurrentPage > 1
            )
                store.dispatch("app/setChatBarCurrentPageChange", rootState.app.chatBarCurrentPage - 1);

            return result;
        },
        getWaitingConversations: (state) => {
            return state.conversations.filter((c) => c.status === "new" || c.users.length === 0);
        },
        getAbandonedConversations: (state) => {
            return state.conversations.filter((c) => ["active", "lost"].includes(c.status) && !c.users.length);
        },
        getUnreadConversations: (state) => {
            return state.conversations.filter((c) => {
                const meta = state.conversationsMeta[c._id] || {};
                console.log("getUnreadConversations", { ...meta });
                return meta.unread > 0;
            });
        },
        activeAgentRate: (state, getters, rootState) => {
            const onlineUsers = rootState.user.users.data.filter(
                (u) => u.isOnline === true && u.status.toLowerCase() === "online"
            );
            const activeConversationsCount = _.sum(onlineUsers.map((x) => x.conversationCount || 0));
            let systemLimit = rootState.setting.settings.autoAssignDefaultLimit;
            systemLimit = Math.max(systemLimit, 0);
            let totalUserMaxCapacity = onlineUsers.map((x) => {
                const userLimit = x.autoAssignLimit;
                let result = 0;
                result = userLimit < 0 ? systemLimit : userLimit;
                return result;
            });
            totalUserMaxCapacity = _.sum(totalUserMaxCapacity);
            log("activeAgentRate", {
                activeConversationsCount,
                systemLimit,
                totalUserMaxCapacity,
            });
            if (activeConversationsCount > 0) {
                if (totalUserMaxCapacity === 0) {
                    return 0;
                } else {
                    return (activeConversationsCount / totalUserMaxCapacity) * 100;
                }
            } else {
                return 0;
            }
        },
        systemMaxChatCapacity: (state, getters, rootState) => {
            const onlineUsers = rootState.user.users.data.filter(
                (u) => u.isOnline === true && u.status.toLowerCase() === "online"
            );
            let systemLimit = rootState.setting.settings.autoAssignDefaultLimit;
            systemLimit = systemLimit ? systemLimit : 0;
            systemLimit = Math.max(systemLimit, 0);
            const totalUserMaxCapacity = onlineUsers.map((x) => {
                let userLimit = x.autoAssignLimit;
                let result = 0;
                result = userLimit < 0 ? systemLimit : userLimit;
                return result;
            });

            return _.sum(totalUserMaxCapacity);
        },
        findConversation: (state) => (id) => {
            return state.conversations.find((c) => c._id === id);
        },
        findConversationByVisitorId: (state) => (id) => {
            return state.conversations.find((c) => c.visitor === id);
        },
        findActiveConversation: (state) => (id) => {
            return state.conversations.find((c) => c.active === true && c._id === id);
        },
    },
    mutations: {
        UPDATE_VISITOR_LANG(state, payload) {
            let { conversationId, preferredLang } = payload;
            log("UPDATE_VISITOR_LANG", { conversationId, preferredLang });
            let maybeChat = state.conversations.find((c) => c._id === conversationId);
            if (!!maybeChat) {
                maybeChat.visitor.preferredLang = preferredLang;
                maybeChat.languageCode = preferredLang;
            }
            if ( !!state.activeConversation && state.activeConversation._id === conversationId) {
                state.activeConversation.visitor.preferredLang = preferredLang;
                state.activeConversation.languageCode = preferredLang;
            }
        },
        SET_AUTO_TRANSLATE(state, payload) {
            if (payload.state) state.autoTranslate.push(payload.id);
            else state.autoTranslate = state.autoTranslate.filter((x) => x !== payload.id);
        },
        SET_LATEST_ROWS: (state, rows) => {
            log("SET_LATEST_ROWS", rows);
            state.latestRows = [];
            state.latestRows = rows;
        },
        SET_LATEST_CHATBOT_CHATS: (state, rows) => {
            state.latestChatbotChats = rows;
        },
        SET_CONVERSATION_STATS: (state, stats) => {
            state.stats = stats;
        },
        SET_FAKE_ACTIVE_CONVERSATION: (state, chat) => {
            state.fakeActiveConversation = chat;
        },
        SET_PRIVATE_MESSAGE_CONVERSATION: (state, id) => {
            state.privateMessageConversation = id;
        },
        ADD_CONVERSATION: (state, payload) => {
            let { conversation, meta } = payload;
            if (!meta.chatBarOrder) meta.chatBarOrder = chatBarIndex++;
            log("ADD_CONVERSATION", { conversation, meta: { ...meta } });
            state.conversations = _.uniqBy([...state.conversations, conversation], (q) => q._id);
            const indexOfCon = state.conversations.findIndex((x) => x._id === conversation._id);
            state.conversationsMeta[conversation._id] = meta;
            Vue.set(state.conversationsMeta, conversation._id, meta);
            setConversationsMeta(state.conversationsMeta);
            Vue.set(state.conversations, indexOfCon, conversation);
            state.myChatIds = _.uniq([...state.myChatIds, conversation._id]);
            setMyChatIds(state.myChatIds);
        },
        ADD_MULTI_CONVERSATION: (state, payload) => {
            let { context, addingChats } = payload;
            const { rootState } = context;
            log("ADD_MULTI_CONVERSATION", addingChats);
            log("ADD_MULTI_CONVERSATION before", {
                ids: addingChats.map((x) => _.takeRight(x._id, 3).join("")),
            });
            const grouped = _.groupBy(addingChats, (q) => q._id);
            addingChats = Object.keys(grouped).map((key) => _.last(grouped[key]));
            addingChats
                .filter((x) => x.users.includes(rootState.auth.user._id))
                .forEach((x) => {
                    const meta = state.conversationsMeta[x._id] || {};
                    meta.chatBarOrder = chatBarIndex++;
                    state.conversationsMeta[x._id] = meta;
                    state.myChatIds.push(x._id);
                    setConversationsMeta(state.conversationsMeta);
                });
            state.myChatIds = _.uniq(state.myChatIds);
            setMyChatIds(state.myChatIds);
            state.conversations = _.uniqBy([...state.conversations, ...addingChats], (q) => q._id);
        },
        UPDATE_CONVERSATION: (state, payload) => {
            let { context, conversation } = payload;
            const { rootState } = context;
            const findIndex = state.conversations.findIndex((c) => c._id === conversation._id);
            log("UPDATE_CONVERSATION", { findIndex, ...payload });
            try {
                if (findIndex !== -1) {
                    const foundConversation = state.conversations[findIndex];
                    const meta = state.conversationsMeta[conversation._id] || {};
                    if (!meta.chatBarOrder && conversation.users.includes(rootState.auth.user._id)) {
                        meta.chatBarOrder = chatBarIndex++;
                        state.conversationsMeta[conversation._id] = meta;
                        setConversationsMeta(state.conversationsMeta);
                    } else {
                        Vue.set(state.conversations, findIndex, conversation);
                    }
                }

                if (
                    state.activeConversation != null &&
                    state.conversations[findIndex]._id === state.activeConversation._id
                ) {
                    state.activeConversation = state.conversations[findIndex];
                }

                log("UPDATE_CONVERSATION after", {
                    conversations: state.conversations,
                });
            } catch (e) {
                console.error(e);
            }
        },
        UPDATE_MULTI_CONVERSATION: (state, payload) => {
            let { updatingChats, context } = payload;
            const { rootState } = context;
            const grouped = _.groupBy(updatingChats, (x) => x._id);
            updatingChats = Object.keys(grouped) // aynı id de 2 veya daha fazla update varsa sonuncuyu alıyoruz
                .map((id) => {
                    const items = grouped[id];
                    return items.length === 1 ? items[0] : _.last(items);
                });
            const currentChatIdsMap = state.conversations.reduce((acc, cur) => {
                acc[cur._id] = cur;
                return acc;
            }, {});

            const validUpdatingIds = [];
            const updatingMaps = {};
            updatingChats.forEach((c) => {
                if (currentChatIdsMap[c._id]) {
                    c.isLooked = currentChatIdsMap[c._id].isLooked;
                    validUpdatingIds.push(c._id);
                    updatingMaps[c._id] = c;
                }
            });
            Object.keys(updatingMaps)
                .map((id) => updatingMaps[id])
                .filter((x) => x.users.includes(rootState.auth.user._id) && !x.chatBarOrder)
                .forEach((x) => {
                    const meta = state.conversationsMeta[x._id] || {};

                    meta.chatBarOrder = chatBarIndex++;
                    state.conversationsMeta[x._id] = meta;
                    setConversationsMeta(state.conversationsMeta);
                });

            if (validUpdatingIds.length) {
                state.conversations = _.uniqBy(
                    [
                        ...state.conversations.filter((c) => updatingMaps[c._id] == null),
                        ...Object.keys(updatingMaps).map((key) => updatingMaps[key]),
                    ],
                    (q) => q._id
                );

                if (state.activeConversation != null && updatingMaps[state.activeConversation._id] != null) {
                    state.activeConversation = updatingMaps[state.activeConversation._id];
                }
            }
        },
        REMOVE_CONVERSATION: (state, id) => {
            state.lookedConversationIds = state.lookedConversationIds.filter((c) => c !== id);
            delete state.conversationsMeta[id];
            setConversationsMeta(state.conversationsMeta);
            deleteLookedChatFromStorage(id);
            state.conversations = state.conversations.filter((c) => c._id !== id);
            state.myChatIds = state.myChatIds.filter((cId) => cId !== id);
            setMyChatIds(state.myChatIds);
            if (state.activeConversation && state.activeConversation._id === id) {
                state.activeConversation = null;
            }
        },
        REMOVE_MULTI_CONVERSATION: (state, ids) => {
            state.lookedConversationIds = _.difference(state.lookedConversationIds, ids);
            const idsMap = ids.reduce((acc, cur) => {
                if (state.activeConversation && state.activeConversation._id === cur) {
                    state.activeConversation = null;
                }
                deleteLookedChatFromStorage(cur);
                delete state.conversationsMeta[cur];
                setConversationsMeta(state.conversationsMeta);
                acc[cur] = true;
                return acc;
            }, {});
            state.conversations = state.conversations.filter((c) => !idsMap[c._id]);
            state.myChatIds = state.myChatIds.filter((cId) => !idsMap[cId]);

            setMyChatIds(state.myChatIds);
        },
        SET_ACTIVE_CONVERSATION: (state, id) => {
            if (!id) {
                state.activeConversation = null;
                return;
            }
            const conversation = state.conversations.find((c) => c._id === id);
            let meta = state.conversationsMeta[id] || {};
            meta.unread = 0;
            meta.isLooked = true;
            state.lookedConversationIds.push(id);
            pushLookedChatId(conversation._id);
            if (conversation.languageCode == null) {
                conversation.languageCode = "tr";
            }

            state.activeConversation = conversation;
            state.conversationsMeta[id] = meta;
            setConversationsMeta(state.conversationsMeta);
            Vue.set(state.activeConversation, "active", true);
            state.openChats = [...state.openChats, state.activeConversation];
        },
        SET_PREVIOUS_CONVERSATION: (state, id) => {
            state.previousConversation = id;
        },
        LEAVE_CONVERSATION: (state, id, removeFromBeep = false) => {
            const conversation = state.conversations.find((c) => c._id === id);

            if (conversation) {
                const meta = state.conversationsMeta[id] || {};
                meta.active = false;
                delete meta.chatBarOrder;
                state.conversationsMeta[id] = meta;
                setConversationsMeta(state.conversationsMeta);
                if (state.activeConversation && state.activeConversation._id === id) {
                    state.activeConversation = null;
                }
                if (removeFromBeep) state.beepPlayedChatIds = state.beepPlayedChatIds.filter((x) => x !== id);
            }
        },
        HIDE_CONVERSATION: (state) => {
            state.activeConversation = null;
        },
        ADD_USER_TO_CONVERSATION: (state, payload) => {
            const { userId, conversationId } = payload;
            const findIndex = state.conversations.findIndex((x) => x._id === conversationId);
            if (findIndex !== -1) {
                const conversation = state.conversations[findIndex];
                conversation.users = _.uniq([...(conversation.users || []), userId]);
                if (conversation.status === "lost") {
                    conversation.status = "active";
                }
                Vue.set(state.conversations, findIndex, conversation);
            }
        },
        LOST_CONVERSATION: (state, conversationId) => {
            log.info("LOST_CONVERSATION", { conversationId });
            const findIndex = state.conversations.findIndex((x) => x._id === conversationId);
            if (findIndex !== -1) {
                const conversation = state.conversations[findIndex];
                conversation.status = "lost";
                Vue.set(state.conversations, findIndex, conversation);
            }
        },
        REMOVE_USER_FROM_CONVERSATION: (state, payload) => {
            const { userId, conversationId } = payload;
            const findIndex = state.conversations.findIndex((x) => x._id === conversationId);
            if (findIndex !== -1) {
                const conversation = state.conversations[findIndex];
                conversation.users = conversation.users.filter((id) => id !== userId);
                // if (conversation.users.length === 0 && !!conversation.connectedUser) {
                //     conversation.status = 'lost'
                // }
                Vue.set(state.conversations, findIndex, conversation);
            }
        },

        REMOVE_CHAT_ID: (state, conversationId) => {
            state.myChatIds = state.myChatIds.filter((cId) => cId !== conversationId);
            setMyChatIds(state.myChatIds);
        },
        ADD_OBSERVE: (state, id) => {
            state.observing.push(id);
            const findIndex = state.conversations.findIndex((x) => x._id === id);
            if (findIndex !== -1) {
                const meta = state.conversationsMeta[id] || {};
                meta.chatBarOrder = meta.chatBarOrder || chatBarIndex++;
                state.conversationsMeta[id] = meta;
                setConversationsMeta(state.conversationsMeta);
            }
            state.observing = _.uniq(state.observing);
            setWatchingChatIds(state.observing);
            log("ADD_OBSERVE", id, "last state", state.observing);
        },
        REMOVE_OBSERVE: (state, id) => {
            const isWatching = state.observing.includes(id);
            if (isWatching) {
                state.observing = state.observing.filter((x) => x !== id);
                state.observing = _.uniq(state.observing);
                state.lookedConversationIds = state.lookedConversationIds.filter((x) => x !== id);
                deleteLookedChatFromStorage(id);
                setWatchingChatIds(state.observing);
                const findIndex = state.conversations.findIndex((x) => x._id === id);
                if (findIndex !== -1) {
                    const meta = state.conversationsMeta[id] || {};
                    meta.isLooked = false;
                    delete meta.chatBarOrder;
                    state.conversationsMeta[id] = meta;
                    setConversationsMeta(state.conversationsMeta);
                }
                log("REMOVE_OBSERVE", id, "last state", state.observing);
            }
        },
        REMOVE_ACTIVE_CONVERSATION: (state) => {
            log("REMOVE_ACTIVE_CONVERSATION");
            state.activeConversation = null;
        },
        REMOVE_MULTI_OBSERVE: (state, ids) => {
            state.lookedConversationIds = _.difference(state.lookedConversationIds, ids);
            const idsMap = ids.reduce((acc, cur) => {
                acc[cur] = true;
                deleteLookedChatFromStorage(cur);
                return acc;
            }, {});
            state.observing = state.observing.filter((id) => !idsMap[id]);
            state.observing = _.uniq(state.observing);
            setWatchingChatIds(state.observing);
        },
        REMOVE_AGENT: (state, payload) => {
            const conversation = state.conversations.find((c) => c._id === payload.conversation);
            if (!!conversation) {
                conversation.users = conversation.users.filter((id) => id !== payload.agent);
            }
        },
        SET_BEEP_CONVERSATION: (state, payload) => {
            state.beepPlayedChatIds.push(payload.id);
        },
        SET_MULTI_BEEP_CONVERSATION: (state, ids) => {
            log.debug("SET_MULTI_BEEP_CONVERSATION", { ids });
            state.beepPlayedChatIds = _.uniq([...state.beepPlayedChatIds, ...ids]);
        },
        ADD_USERS_PRIVATE_CHANNEL_MESSAGE: (state, message) => {
            log.debug("ADD_USERS_PRIVATE_CHANNEL_MESSAGE", { message });
            state.usersPrivateChatChannelMessages = [...state.usersPrivateChatChannelMessages, message];
        },
        DELETE_PRIVATE_CHANNEL_MESSAGE: (state, messageId) => {
            log.debug("DELETE_PRIVATE_CHANNEL_MESSAGE", { messageId });
            state.usersPrivateChatChannelMessages = state.usersPrivateChatChannelMessages.reduce(
                (messages, currMessage) => {
                    if (currMessage.createdDate === messageId) {
                        currMessage = {
                            ...currMessage,
                            message: i18n.t("message.messageDeleted"),
                        };
                        messages.push(currMessage);
                        return messages;
                    }
                    messages.push(currMessage);
                    return messages;
                },
                []
            );
        },
        ADD_MULTI_USERS_PRIVATE_CHANNEL_MESSAGE: (state, messages) => {
            state.usersPrivateChatChannelMessages = messages;
        },
        /**
         * Admin giriş yaptığında resetlemek için çağırıyoruz.
         * @param state
         */
        RESET_STATE: (state) => {
            log.info("RESET_STATE running");
            state.observing = defaultState().observing;
            state.conversations = defaultState().conversations;
            state.activeConversation = defaultState().activeConversation;
            state.previousConversation = defaultState().previousConversation;
            state.privateMessageConversation = defaultState().privateMessageConversation;
            state.overdueTime = defaultState().overdueTime;
            state.activeAgentRateConversationCount = defaultState().activeAgentRateConversationCount;
            state.openChats = defaultState().openChats;
            state.beepPlayedChatIds = defaultState().beepPlayedChatIds;
            state.usersPrivateChatChannelMessages = defaultState().usersPrivateChatChannelMessages;
            state.myChatIds = defaultState().myChatIds;
            state.conversationsMeta = {};
            setConversationsMeta({});
            setMyChatIds(state.myChatIds);
        },
        TOGGLE_CHAT_HISTORY: (state, payload) => {
            state.isChatHistoryOpen = payload || !state.isChatHistoryOpen;
        },
        SET_CONVERSATIONS: (state, payload) => {
            const { conversations, partNumber, userId } = payload;
            if (partNumber === 1) {
                if (conversations.length === 0) {
                    const isDeleteNeedForActiveConversation = _.get(state, "activeConversation.users", []).includes(
                        userId
                    );
                    if (isDeleteNeedForActiveConversation) {
                        state.activeConversation = null;
                    }
                    state.conversations = [];
                    state.conversationsMeta = {};
                    state.myChatIds = [];
                    setConversationsMeta(state.conversationsMeta);
                    setMyChatIds(state.myChatIds);
                } else {
                    const newMetas = conversations.reduce((acc, cur) => {
                        log("SET_CONVERSATIONS", { cur });
                        const maybeMeta = state.conversationsMeta[cur._id];
                        if (!maybeMeta) {
                            acc[cur._id] = {
                                active: true,
                                autoAssign: false,
                                unread: 0,
                                chatBarOrder: chatBarIndex++,
                                isLooked: false,
                            };
                        }
                        return acc;
                    }, {});
                    state.conversationsMeta = {
                        ...state.conversationsMeta,
                        ...newMetas,
                    };
                    state.conversations = conversations;

                    _.difference(
                        Object.keys(state.conversationsMeta),
                        conversations.map((x) => x._id)
                    ).forEach((id) => {
                        delete state.conversationsMeta[id];
                    });
                    _.difference(
                        state.myChatIds,
                        conversations.map((x) => x._id)
                    ).forEach((id) => {
                        state.myChatIds = state.myChatIds.filter((cId) => cId !== id);
                    });

                    setConversationsMeta(state.conversationsMeta);
                    setMyChatIds(state.myChatIds);
                }
            } else {
                state.conversations = _.concat(state.conversations, conversations);
            }
        },
        UPDATE_META: (state, payload) => {
            let { meta, conversationId } = payload;
            console.log("UPDATE_META", payload);
            // state.conversationsMeta[conversationId] = meta
            Vue.set(state.conversationsMeta, conversationId, meta);
            state.conversations = [...state.conversations];
            setConversationsMeta(state.conversationsMeta);
        },
        SET_REPRESENTATIVE_NOTE: (state, payload) => {
            console.log("active conversation set", payload);
            state.activeConversation.note = payload.note;
        },
        UPDATE_VISITOR_DETAIL: (state, { conversationId, fullname, email }) => {
            log("UPDATE_VISITOR_DETAIL", { conversationId, fullname, email });
            state.conversations = state.conversations.map((x) => {
                if (x._id === conversationId) {
                    x.fullname = fullname;
                    x.email = email;
                }
                return x;
            });
            if (state.activeConversation && state.activeConversation._id === conversationId) {
                state.activeConversation.fullname = fullname;
                state.activeConversation.email = email;
            }
        },
    },
    actions: {
        setLatestRows({ commit }, rows) {
            commit("SET_LATEST_ROWS", rows);
        },
        setLatestChatBotChats({ commit }, rows) {
            commit("SET_LATEST_CHATBOT_CHATS", rows);
        },
        viewFakeConversation({ commit }, chat) {
            commit("SET_FAKE_ACTIVE_CONVERSATION", chat);
        },
        beepConversation({ commit }, payload) {
            commit("SET_BEEP_CONVERSATION", payload);
        },
        multiBeepConversation({ commit }, ids) {
            commit("SET_MULTI_BEEP_CONVERSATION", ids);
        },
        addConversation({ commit, state, rootState, getters }, payload) {
            let { conversation, meta } = payload;
            commit("ADD_CONVERSATION", payload);
            let myActiveConversations = getters.getMyActiveConversations;
            store.dispatch(
                "app/setChatBarCurrentPageChange",
                Math.ceil(myActiveConversations.length / rootState.app.chatBarPageSize)
            );
            if (conversation.forceOpen) {
                commit("SET_ACTIVE_CONVERSATION", conversation._id);
            }
        },
        syncConversationWithIds({ rootState, commit, getters, state }, ids) {
            const allIds = state.conversations.map((x) => x._id).filter((id) => !state.observing.includes(id));
            let deadIds = _.difference(allIds, ids);

            if (deadIds.length) {
                log("DEAD CHAT IDS DETECTED", deadIds);
                commit("REMOVE_MULTI_CONVERSATION", deadIds);
            }
        },
        updateConversation({ commit, rootState }, conversation) {
            commit("UPDATE_CONVERSATION", {
                conversation,
                context: { rootState },
            });
        },
        viewConversation({ rootState, commit, getters, state }, payload) {
            if (state.activeConversation && payload.id !== state.activeConversation._id) {
                commit("SET_PREVIOUS_CONVERSATION", state.activeConversation._id);
            } else {
                commit("SET_PREVIOUS_CONVERSATION", payload._id);
            }
            commit("SET_ACTIVE_CONVERSATION", payload.id);
        },
        joinConversation({ rootState, commit, getters, state }, payload) {
            log("joinConversation", payload);
            const site = rootState.site.site;
            const { conversation, reconnect, observe, autoAssign, minimized } = payload;
            if (observe) {
                commit("ADD_OBSERVE", conversation._id);
            } else {
                commit("REMOVE_OBSERVE", conversation._id);
                conversation.users = _.uniq([...conversation.users, rootState.auth.user._id]);
            }
            if (state.activeConversation && conversation._id !== state.activeConversation._id) {
                commit("SET_PREVIOUS_CONVERSATION", state.activeConversation._id);
            } else {
                commit("SET_PREVIOUS_CONVERSATION", payload._id);
            }
            const sendToSocket = !(payload.hasOwnProperty("sendToSocket") && !payload.sendToSocket);
            if (sendToSocket)
                sendSocketMessage("user join-conversation", {
                    userId: rootState.auth.user._id,
                    conversationId: conversation._id,
                    siteId: site._id,
                    reconnect,
                    autoAssign: autoAssign,
                    watchmode: observe,
                });
            const maybeConversation = state.conversations.find((x) => x._id === conversation._id);
            const meta = state.conversationsMeta[conversation._id] || {};
            log("joinConversation", { maybeConversation });
            meta.chatBarOrder = meta.chatBarOrder || chatBarIndex++;
            if (payload.hasOwnProperty("autoAssign") && payload.autoAssign) {
                meta.autoAssign = true;
            } else if (conversation.hasOwnProperty("autoAssign") && conversation.autoAssign) {
                meta.autoAssign = false;
            }
            log("join conservation.... ", { order: conversation.chatBarOrder });
            meta.active = true;
            commit("ADD_CONVERSATION", { conversation, meta });
            if (!minimized) {
                commit("SET_ACTIVE_CONVERSATION", conversation._id);
            } else {
                meta.active = true;
                commit("ADD_CONVERSATION", { conversation, meta });
            }
        },
        leaveConversation({ state, rootState, commit, getters }, id, removeFromBeep = false) {
            log("leaveConversation", id);
            const site = rootState.site.site;
            const c = getters.findConversation(id);
            const watchmode = state.observing.includes(id);
            commit("LEAVE_CONVERSATION", id, removeFromBeep);
            commit("REMOVE_OBSERVE", id);
            commit("REMOVE_AGENT", {
                conversation: id,
                agent: rootState.auth.user._id,
            });

            if (c.status === "destroyed") commit("REMOVE_CONVERSATION", id);
            if (c.status !== "destroyed") {
                sendSocketMessage("user leave-chat", {
                    userId: rootState.auth.user._id,
                    conversationId: id,
                    siteId: site._id,
                    watchmode,
                });
            }
            commit("REMOVE_CONVERSATION", id);
        },
        endConversation({ state, rootState, commit, dispatch, getters }, id) {
            log("endConversation", { id });
            const site = rootState.site.site;
            commit("LEAVE_CONVERSATION", id);
            commit("REMOVE_CONVERSATION", id);
            commit("REMOVE_OBSERVE", id);

            if (state.activeConversation && state.activeConversation._id === id) {
                commit("SET_ACTIVE_CONVERSATION", null);
            }

            sendSocketMessage("user destroy-chat", {
                conversationId: id,
                userId: rootState.auth.user._id,
                siteId: site._id,
            });

            if (rootState.webrtc.dialingConversationId === id) {
                const phone = PHONE_UTILS.getPhone();
                if (phone) {
                    Object.keys(phone.conversations).forEach((number) => phone.hangup(number));
                    phone.camera.stop();
                }
            }
        },
        hideConversation({ commit }, id) {
            commit("HIDE_CONVERSATION", id);
        },
        addUserToConversation({ state, rootState, commit, getters }, payload) {
            commit("ADD_USER_TO_CONVERSATION", payload);
        },
        removeUserFromConversation({ state, rootState, commit, getters }, payload) {
            const { userId, conversationId } = payload;
            commit("REMOVE_USER_FROM_CONVERSATION", payload);
            if (userId === rootState.auth.user._id) {
                commit("REMOVE_CHAT_ID", conversationId);
            }
            const c = state.conversations.find((x) => x._id === conversationId);
            if (!!c) {
                if (c.users.length === 0) {
                    const lostMarkingWaitTime = rootState.setting.settings.overdueTime * 1000;
                    setTimeout(() => {
                        const chat = state.conversations.find((x) => x._id === conversationId);
                        if (!!chat && chat.users.length === 0) {
                            commit("LOST_CONVERSATION", conversationId);
                        }
                    }, lostMarkingWaitTime);
                }
            }
        },
        removeConversation({ state, rootState, commit, getters }, id) {
            log("removeConversation called -> ", id);
            commit("REMOVE_CONVERSATION", id);
            commit("REMOVE_OBSERVE", id);
            if (state.previousConversation === id) commit("SET_PREVIOUS_CONVERSATION", null);

            if (rootState.webrtc.dialingConversationId === id) {
                const phone = PHONE_UTILS.getPhone();
                if (phone) {
                    Object.keys(phone.conversations).forEach((number) => phone.hangup(number));
                    phone.camera.stop();
                }
            }
        },
        removeMultiConversation({ state, rootState, commit, getters }, ids = []) {
            log("removeMultiConversation called -> ", { ids });
            commit("REMOVE_MULTI_CONVERSATION", ids);
            commit("REMOVE_MULTI_OBSERVE", ids);
            if (state.activeConversation && ids.includes(state.activeConversation._id)) {
                commit("REMOVE_ACTIVE_CONVERSATION");
            }

            if (ids.includes(state.previousConversation)) commit("SET_PREVIOUS_CONVERSATION", null);

            if (ids.includes(rootState.webrtc.dialingConversationId)) {
                const phone = PHONE_UTILS.getPhone();
                if (phone) {
                    Object.keys(phone.conversations).forEach((number) => phone.hangup(number));
                    phone.camera.stop();
                }
            }
        },
        banConversation({ state, rootState, commit, getters }, id) {
            const site = rootState.site.site;
            const conversation = state.conversations.find((c) => c._id.toString() === id.toString());
            const visitorId = conversation.visitor._id;
            const socketMessageId = rootState.auth.adminUser != null ? "admin visitor_block" : "user visitor-block";
            sendSocketMessage(socketMessageId, {
                visitorId: visitorId,
                conversationId: id,
                userId: rootState.auth.user._id,
                siteId: site._id,
            });

            commit("LEAVE_CONVERSATION", id);
            commit("REMOVE_CONVERSATION", id);
        },
        loadOldConversations(context, visitor) {
            if ("object" === typeof visitor && visitor.hasOwnProperty("_id")) {
                visitor = visitor._id;
            }

            return loadOldConversations(visitor);
        },
        loadOldConversationsAsPaged(context, { visitor, page, perPage, conversationId }) {
            if ("object" === typeof visitor && visitor.hasOwnProperty("_id")) {
                visitor = visitor._id;
            }

            return loadOldConversationsAsPagedVal(visitor, page, perPage, conversationId);
        },
        loadOldMessages(context, conversationId) {
            return loadOldMessages(conversationId);
        },
        increaseUnreadMessageCount({ state, commit, getters }, payload) {
            const meta = state.conversationsMeta[payload.conversationId];

            if (meta) {
                meta.unread = meta.unread ? meta.unread + 1 : 1;
                meta.lastMessageTime = Date.now();
                commit("UPDATE_META", {
                    meta,
                    conversationId: payload.conversationId,
                });
            }
        },
        setPrivateMessageConversation({ commit }, id) {
            commit("SET_PRIVATE_MESSAGE_CONVERSATION", id);
        },
        addObservation({ commit }, id) {
            commit("ADD_OBSERVE", id);
        },
        addMultiConversation(context, addingChats) {
            context.commit("ADD_MULTI_CONVERSATION", { addingChats, context });
        },
        updateMultiConversation(context, updatingChats) {
            context.commit("UPDATE_MULTI_CONVERSATION", {
                context,
                updatingChats,
            });
        },
        toggleObservation({ state, commit }, id) {
            log("toggleObservation", { id });
            if (state.observing.includes(id)) {
                commit("REMOVE_OBSERVE", id);
            } else {
                commit("ADD_OBSERVE", id);
            }
        },
        resetState({ state, commit }) {
            log.warn("ChatStore state resetting");
            commit("RESET_STATE");
        },
        addUsersPrivateChannelMessage({ state, commit }, message) {
            commit("ADD_USERS_PRIVATE_CHANNEL_MESSAGE", message);
        },
        deleteUsersPrivateChannelMessage({ state, commit }, messageId) {
            commit("DELETE_PRIVATE_CHANNEL_MESSAGE", messageId);
        },
        addMultiUsersPrivateChannelMessage({ state, commit }, messages) {
            commit("ADD_MULTI_USERS_PRIVATE_CHANNEL_MESSAGE", messages);
        },
        toggleChatHistory({ commit }, payload) {
            commit("TOGGLE_CHAT_HISTORY", payload);
        },
        setConversations({ commit, rootState }, payload) {
            const userId = _.get(rootState, "auth.user._id");
            payload.userId = userId;
            commit("SET_CONVERSATIONS", payload);
        },
        setConversationStats({ commit }, stats) {
            commit("SET_CONVERSATION_STATS", stats);
        },
        addNoteToConversation({ commit }, payload) {
            return addNoteToConversation(payload.id, payload.representativeNote, payload.representativeName).then(
                (response) => {
                    if (!payload.isArchive) {
                        commit("SET_REPRESENTATIVE_NOTE", response.data);
                    }
                }
            );
        },
        updateVisitorDetail({ commit }, payload) {
            commit("UPDATE_VISITOR_DETAIL", payload);
        },
    },
};

export default chat;
